import React, { useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { fetchMatterCD, deleteMatterCD } from "../../action/matterCD";
import { setAlert } from "../../action/alert";
import Alert from "../layout/Alert";
import "./GetCDForm.css";
import { Grid, CircularProgress, Box, IconButton } from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import DeleteIcon from "@mui/icons-material/Delete";
import Moment from "react-moment";

function handleDownloadHexCertificate(hex, filename = "certificate.txt") {
  const blob = new Blob([hex], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

function GetCDForm() {
  const dispatch = useDispatch();
  const [productId, setProductId] = useState("");
  const [fwVersion, setFwVersion] = useState("");
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [fwVersionError, setFwVersionError] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState("");

  const handleDelete = async (cd) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this certificate declaration? This action cannot be undone."
      )
    ) {
      return;
    }
    const deleteKey = `${cd._id || cd.id}_${cd._productId}_${cd.fwVersion}_${cd.category}`;
    setDeleteLoading(deleteKey);
    try {
      await deleteMatterCD(cd._id || cd.id, cd._productId, cd.fwVersion);
      dispatch(
        setAlert("Certificate declaration deleted successfully!", "success")
      );
      setResults((prev) =>
        prev.filter(
          (item) =>
          `${item._id || item.id}_${item._productId}_${item.fwVersion}_${item.category}` !==
          deleteKey
        )
      );
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message;
      setError(errMsg);
      dispatch(setAlert(errMsg, "error"));
    }
    setDeleteLoading("");
  };

  const resetForm = () => {
    setProductId("");
    setFwVersion("");
    setResults([]);
    setError("");
    setFwVersionError("");
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFwVersionError("");
    setResults([]);
    if (!/^\d+\.\d+$/.test(fwVersion)) {
      setFwVersionError(
        "Firmware Version must be a decimal value (e.g. 1.0, 2.5)"
      );
      return;
    }
    setError("");
    setLoading(true);
    const ids = productId
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    let allResults = [];
    try {
      for (let id of ids) {
        const res = await fetchMatterCD(id, fwVersion);
        if (Array.isArray(res.data)) {
          allResults = allResults.concat(
            res.data.map((cd) => ({ ...cd, _productId: id }))
          );
        } else {
          allResults.push({ ...res.data, _productId: id });
        }
      }
      setResults(allResults);
      if (allResults.length === 0) {
        dispatch(setAlert("Certificate declaration not found", "danger"));
        setError("");
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message;
      dispatch(setAlert(errMsg, "danger"));
      setError("");
    }
    setLoading(false);
  };

  return (
    <div className="content-block bottom-shadow">
      <div className="py-15">
        <form onSubmit={handleSubmit} style={{ maxWidth: 700 }} className="get-cd-form">
          <Grid container spacing={3} alignItems="center">
            {/* Product ID */}
            <Grid item xs={12} sm={4} className="get-cd-form-label">
              <label className="title">Product ID:</label>
            </Grid>
            <Grid item xs={12} sm={8} className="get-cd-form-input">
              <input
                type="text"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                required
                className="add-input xl"
                placeholder="Enter Product ID"
                style={{ width: "100%" }}
              />
            </Grid>

            {/* FW Version */}
            <Grid item xs={12} sm={4} className="get-cd-form-label">
              <label className="title">Firmware Version:</label>
            </Grid>
            <Grid item xs={12} sm={8} className="get-cd-form-input">
              <input
                type="text"
                value={fwVersion}
                onChange={(e) => setFwVersion(e.target.value)}
                required
                className="add-input xl"
                placeholder="Enter Firmware Version"
                style={{ width: "100%" }}
              />
              {fwVersionError && (
                <div style={{ color: "red", marginTop: 4 }}>
                  {fwVersionError}
                </div>
              )}
            </Grid>

            {/* Buttons */}
            <Grid item xs={12} style={{ marginTop: 24 }}>
              <div className="row" style={{ display: "flex" }}>
                <button
                  className="btn btn-style-2 orange-bg"
                  type="submit"
                  disabled={loading}
                  style={{ marginRight: "12px" }}
                >
                  {loading ? (
                    <Fragment>
                      <CircularProgress
                        size="2em"
                        style={{ color: "#AFB42B", marginTop: "5px" }}
                      />
                    </Fragment>
                  ) : (
                    "Get CD"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-style-2 dark-text gray-bg"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Reset
                </button>
              </div>
            </Grid>
          </Grid>
        </form>

        <div style={{ marginTop: 16 }}>
          <Alert />
        </div>

        {error && (
          <div className="alert alert-danger" style={{ marginTop: 8 }}>
            {error}
          </div>
        )}

        <Box mt={2}>
          {results.length > 0 && (
            <div
              style={{
                marginTop: 16,
                paddingLeft: 24,
                paddingRight: 24,
              }}
            >
              {results.map((cd, idx) => (
                <div
                  key={idx}
                  className="unique-box mb-15"
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: 8,
                    padding: 16,
                    background: "#fafafa",
                    marginBottom: 16,
                    position: "relative",
                  }}
                >

                  <IconButton
                    aria-label="delete"
                    onClick={() => handleDelete(cd)}
                    disabled={deleteLoading === (cd._id || cd.id)}
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: "grey",
                    }}
                  >
                    {deleteLoading === (cd._id || cd.id) ? (
                      <CircularProgress size={20} style={{ color: "#fff" }} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                  
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <label className="title">Product ID</label>
                      <div className="device-info">{cd._productId || cd.productId}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <label className="title">Firmware Version</label>
                      <div className="device-info">{cd.fwVersion}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <label className="title">Category</label>
                      <div className="device-info">{cd.category}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <label className="title">Last Updated On</label>
                      <div className="device-info">
                        <Moment
                          format="MMMM Do YYYY, h:mm:ss a"
                          date={cd.lastUpdatedOn}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ marginTop: 12 }}>
                    <label className="title">Certificate (CD File)</label>
                    <div className="device-info">
                      {cd.certificateUrl || cd.certificateData ? (
                        <a
                          href={
                            cd.certificateUrl ||
                            `data:application/x-pem-file;base64,${cd.certificateData}`
                          }
                          download={`certificate_${cd._productId || cd.productId}_${cd.fwVersion}.pem`}
                        >
                          <GetAppIcon style={{ color: "#AFB42B" }} />
                        </a>
                      ) : cd.certificate ? (
                        <button
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleDownloadHexCertificate(
                              cd.certificate,
                              `certificate_${cd._productId || cd.productId}_${cd.fwVersion}.pem`
                            )
                          }
                        >
                          <GetAppIcon style={{ color: "#AFB42B" }} />
                        </button>
                      ) : (
                        <span style={{ color: "red" }}>
                          Certificate declaration not found
                        </span>
                      )}
                    </div>
                  </div>
                </div>

              ))}
            </div>
          )}
        </Box>
      </div>
    </div>
  );
}

export default GetCDForm;
